import { FetchResult } from '@apollo/client';
import { centsToDollars, createError, dollarsToCents, nullableStringSchema } from '@common/neo.tax';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { useUpdateAccountingExpense } from '@neo.tax/rd/hooks';
import { AccountingQre } from '@neo.tax/rd/hooks/useAccountingQres';
import { RdCategory, UpdateAccountingExpenseMutation } from '@neo.tax/shared';

const makeDefaultFormValues = (accountingQre: AccountingQre): AccountingQreDetailsFormType => {
  if (accountingQre.amountCents == null) {
    throw createError('NotAuthorizedForQREAmounts', 'QRE amount is missing, this might indicate an unauthorized user');
  }
  return {
    id: accountingQre.id,
    rdCategory: accountingQre.rdCategory,
    amountDollars: centsToDollars(accountingQre.amountCents),
    countryCodeAlpha2: accountingQre.country ?? '',
    stateCodeAlpha2: accountingQre.state ?? '',
  };
};

interface UseAccountingQreDetailsFormProps {
  accountingQre: AccountingQre;
}

const validateStateRequiredForUSContractor = (values: {
  rdCategory: RdCategory;
  countryCodeAlpha2: string;
  stateCodeAlpha2: string | null;
}): boolean => {
  const { countryCodeAlpha2, rdCategory, stateCodeAlpha2 } = values;
  const lowerCaseCountry = countryCodeAlpha2.toLowerCase();
  // If the category is contractor and the country is the US, the state is required
  if (rdCategory === RdCategory.Contractor && lowerCaseCountry === 'us') {
    return !!stateCodeAlpha2 && stateCodeAlpha2.length >= 2;
  }

  if (lowerCaseCountry === 'us' && stateCodeAlpha2 === null) {
    // When country is US, a state must be selected
    return false;
  }
  return true;
};

const formSchema = z
  .object({
    id: z.string(),
    amountDollars: z.coerce.number(),
    rdCategory: z.enum(RdCategory),
    countryCodeAlpha2: z.string().length(2), // 2-letter country code
    stateCodeAlpha2: nullableStringSchema, // only required when country is US and category is contractor
  })
  .refine(validateStateRequiredForUSContractor, {
    path: ['stateCodeAlpha2'],
    error: 'State is required for US contractors',
  });

type AccountingQreDetailsFormType = z.infer<typeof formSchema>;

export const useAccountingQreDetailsForm = ({
  accountingQre,
}: UseAccountingQreDetailsFormProps): UseFormReturn<AccountingQreDetailsFormType> => {
  const [updateAccountingExpense] = useUpdateAccountingExpense();

  const form = useForm<AccountingQreDetailsFormType>({
    mode: 'onChange',
    defaultValues: makeDefaultFormValues(accountingQre),
    resolver: standardSchemaResolver(formSchema),
  });
  const { handleSubmit, watch } = form;
  const currentAccountingExpenseId = watch('id');

  const onValid: SubmitHandler<AccountingQreDetailsFormType> = useCallback(
    (values): Promise<FetchResult<UpdateAccountingExpenseMutation>> =>
      updateAccountingExpense(accountingQre.accountingExpenseId, {
        amountCents: dollarsToCents(values.amountDollars),
        countryAlpha2: values.countryCodeAlpha2,
        state: values.countryCodeAlpha2 === 'US' ? values.stateCodeAlpha2 : null,
      }),
    [updateAccountingExpense, accountingQre.accountingExpenseId],
  );

  useEffect(() => {
    const subscription = watch(() => handleSubmit(onValid)());
    return (): void => subscription.unsubscribe();
  }, [handleSubmit, onValid, watch]);

  useEffect(() => {
    if (currentAccountingExpenseId !== accountingQre.id) {
      form.reset(makeDefaultFormValues(accountingQre));
    }
    // We only want to reset the form when the accountingExpense QRE changes
  }, [accountingQre, currentAccountingExpenseId, form]);

  return form;
};
